import { getMyDuAn, getMyChiNhanh } from '@/apis/userAuthApi'
import { profileStorage, tokenStorage } from '@/services/tokenStorage'

let myDuAnChiNhanhCache: { token: string; promise: Promise<[unknown, unknown]> } | null = null

/**
 * `du-an-cua-toi` + `chi-nhanh-cua-toi` dùng chung cho header và mọi picker chi nhánh.
 * Cache theo access token: đổi chi nhánh / đăng nhập lại cấp token mới → tự gọi lại.
 */
export function fetchMyDuAnVaChiNhanh(): Promise<[unknown, unknown]> {
  const token = tokenStorage.getAccessToken() ?? ''
  if (myDuAnChiNhanhCache?.token === token) return myDuAnChiNhanhCache.promise
  const promise = Promise.all([getMyDuAn(), getMyChiNhanh()]) as Promise<[unknown, unknown]>
  myDuAnChiNhanhCache = { token, promise }
  promise.catch(() => {
    if (myDuAnChiNhanhCache?.promise === promise) myDuAnChiNhanhCache = null
  })
  return promise
}

export type ChiNhanhOption = { id: number; label: string }

/** Chi nhánh trong 1 mảng (dự án). */
export type ChiNhanhInMang = {
  id: number
  ma: string
  ten: string
  label: string
}

/** Nhóm chi nhánh theo mảng (= dự án). */
export type ChiNhanhMangGroup = {
  mangId: number
  mangMa: string
  mangTen: string
  chiNhanhs: ChiNhanhInMang[]
}

function parseArray(res: unknown): unknown[] {
  if (Array.isArray(res)) return res
  if (res && typeof res === 'object') {
    const r = res as Record<string, unknown>
    if (Array.isArray(r.data)) return r.data
    if (Array.isArray(r.results)) return r.results
  }
  return []
}

function resolveDuAnIdFromChiNhanhRow(c: Record<string, unknown>): number {
  const direct = c.duAnId ?? c.du_an_id
  if (direct != null && String(direct).trim() !== '' && !Number.isNaN(Number(direct))) {
    return Number(direct)
  }
  const nested = c.duAn
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const id = (nested as Record<string, unknown>).id
    if (id != null && !Number.isNaN(Number(id))) return Number(id)
  }
  return NaN
}

type Flat = {
  id: number
  duAnId: number
  ma: string
  ten: string
  duAn: { id: number; ma: string; ten: string }
}

function chiNhanhTen(c: Record<string, unknown>, ma: string): string {
  const ten = c.ten ?? c.tenChiNhanh ?? c.name
  const s = ten != null ? String(ten).trim() : ''
  return s || ma || String(c.id ?? '')
}

function flattenFromDuAnAndChiNhanh(duAnList: unknown[], chiNhanhList: unknown[]): Flat[] {
  const duAnById = new Map<number, { id: number; ma: string; ten: string }>()
  for (const raw of duAnList) {
    if (!raw || typeof raw !== 'object') continue
    const item = raw as Record<string, unknown>
    const id = Number(item.id)
    if (Number.isNaN(id)) continue
    duAnById.set(id, { id, ma: String(item.ma ?? ''), ten: String(item.ten ?? '') })
  }

  const out: Flat[] = []
  for (const raw of chiNhanhList) {
    if (!raw || typeof raw !== 'object') continue
    const c = raw as Record<string, unknown>
    const cnId = Number(c.id)
    if (Number.isNaN(cnId)) continue
    const duAnId = resolveDuAnIdFromChiNhanhRow(c)
    if (Number.isNaN(duAnId)) continue
    const nestedDuAn = c.duAn
    const fromNested =
      nestedDuAn && typeof nestedDuAn === 'object' && !Array.isArray(nestedDuAn)
        ? (nestedDuAn as Record<string, unknown>)
        : undefined
    const duAnMeta =
      duAnById.get(duAnId) ??
      (fromNested
        ? { id: duAnId, ma: String(fromNested.ma ?? ''), ten: String(fromNested.ten ?? '') }
        : { id: duAnId, ma: '', ten: '' })
    const ma = String(c.ma ?? c.maChiNhanh ?? '')
    out.push({ id: cnId, duAnId, ma, ten: chiNhanhTen(c, ma), duAn: duAnMeta })
  }
  return out
}

function flattenDuAnChiNhanh(duAnList: unknown[]): Flat[] {
  const out: Flat[] = []
  for (const raw of duAnList) {
    if (!raw || typeof raw !== 'object') continue
    const item = raw as Record<string, unknown>
    const duAnId = Number(item.id)
    if (Number.isNaN(duAnId)) continue
    const duAnMeta = { id: duAnId, ma: String(item.ma ?? ''), ten: String(item.ten ?? '') }
    const userNhomQuyens = item.userNhomQuyens
    const chiNhanhs = item.chiNhanhs
    if (Array.isArray(userNhomQuyens) && userNhomQuyens.length > 0) {
      for (const unq of userNhomQuyens) {
        if (!unq || typeof unq !== 'object') continue
        const cn = (unq as Record<string, unknown>).chiNhanh
        if (!cn || typeof cn !== 'object') continue
        const c = cn as Record<string, unknown>
        const id = Number(c.id)
        if (Number.isNaN(id)) continue
        const ma = String(c.ma ?? c.maChiNhanh ?? '')
        out.push({ id, duAnId, ma, ten: chiNhanhTen(c, ma), duAn: duAnMeta })
      }
    } else if (Array.isArray(chiNhanhs) && chiNhanhs.length > 0) {
      for (const cn of chiNhanhs) {
        if (!cn || typeof cn !== 'object') continue
        const c = cn as Record<string, unknown>
        const id = Number(c.id)
        if (Number.isNaN(id)) continue
        const ma = String(c.ma ?? c.maChiNhanh ?? '')
        out.push({ id, duAnId, ma, ten: chiNhanhTen(c, ma), duAn: duAnMeta })
      }
    }
  }
  return out
}

async function loadFlatChiNhanh(): Promise<Flat[]> {
  const [duAnRes, chiNhanhRes] = await fetchMyDuAnVaChiNhanh()
  const duAnList = parseArray(duAnRes)
  const chiNhanhList = parseArray(chiNhanhRes)
  // Ưu tiên chi nhánh gắn qua dự án (userNhomQuyens) — cùng id HTChiNhanh với chứng từ bán hàng.
  // getMyChiNhanh (SSO) đôi khi lệch id → lọc CN (vd HCM) ra rỗng dù có dữ liệu.
  const fromDuAn = flattenDuAnChiNhanh(duAnList)
  if (fromDuAn.length > 0) {
    const byId = new Map<number, Flat>()
    for (const x of fromDuAn) byId.set(x.id, x)
    return Array.from(byId.values())
  }
  const fromJoin = flattenFromDuAnAndChiNhanh(duAnList, chiNhanhList)
  const byId = new Map<number, Flat>()
  for (const x of fromJoin) byId.set(x.id, x)
  return Array.from(byId.values())
}

function flatToOption(x: Flat): ChiNhanhOption {
  return {
    id: x.id,
    label: [x.duAn.ten || x.duAn.ma, x.ten || x.ma].filter(Boolean).join(' · '),
  }
}

function groupByMang(flat: Flat[]): ChiNhanhMangGroup[] {
  const map = new Map<number, ChiNhanhMangGroup>()
  for (const x of flat) {
    let g = map.get(x.duAnId)
    if (!g) {
      g = {
        mangId: x.duAnId,
        mangMa: x.duAn.ma,
        mangTen: x.duAn.ten || x.duAn.ma || `Mảng #${x.duAnId}`,
        chiNhanhs: [],
      }
      map.set(x.duAnId, g)
    }
    g.chiNhanhs.push({
      id: x.id,
      ma: x.ma,
      ten: x.ten,
      label: x.ten || x.ma || String(x.id),
    })
  }
  return Array.from(map.values())
    .map((g) => ({
      ...g,
      chiNhanhs: [...g.chiNhanhs].sort((a, b) => a.label.localeCompare(b.label, 'vi')),
    }))
    .sort((a, b) => a.mangTen.localeCompare(b.mangTen, 'vi'))
}

/** Danh sách chi nhánh phẳng (tên mảng · tên/mã CN). */
export async function loadChiNhanhOptions(): Promise<ChiNhanhOption[]> {
  const flat = await loadFlatChiNhanh()
  return flat.map(flatToOption)
}

/** Chi nhánh gom theo mảng (dự án) — dùng UI lưới checkbox. */
export async function loadChiNhanhMangGroups(): Promise<ChiNhanhMangGroup[]> {
  const flat = await loadFlatChiNhanh()
  return groupByMang(flat)
}

export type DuAnOption = { id: number; label: string; ten: string }

/** Dự án (mảng) user là thành viên, nhãn = mã dự án — từ `du-an-cua-toi`, bổ sung mảng chỉ thấy qua chi nhánh. */
export async function loadDuAnOptions(): Promise<DuAnOption[]> {
  const [duAnRes] = await fetchMyDuAnVaChiNhanh()
  const byId = new Map<number, { label: string; ten: string }>()
  for (const raw of parseArray(duAnRes)) {
    if (!raw || typeof raw !== 'object') continue
    const item = raw as Record<string, unknown>
    const id = Number(item.id)
    if (!Number.isFinite(id) || id <= 0) continue
    const ten = String(item.ten ?? '').trim()
    byId.set(id, { label: String(item.ma ?? '').trim() || ten || `#${id}`, ten })
  }
  for (const g of groupByMang(await loadFlatChiNhanh())) {
    if (!byId.has(g.mangId)) byId.set(g.mangId, { label: g.mangMa || g.mangTen, ten: g.mangTen })
  }
  return Array.from(byId, ([id, v]) => ({ id, ...v })).sort((a, b) => a.label.localeCompare(b.label, 'vi'))
}

/** Mục chọn dự án: danh sách hiện «mã — tên», thẻ đã chọn chỉ hiện mã. */
export const duAnPickLabel = (ma: string, ten: string): { label: string; tagLabel: string } => {
  const m = ma.trim()
  const t = ten.trim()
  return { label: m && t && m !== t ? `${m} — ${t}` : m || t, tagLabel: m || t }
}

/** Id dự án đang đứng (từ profile). */
export function currentDuAnIdFromProfile(): number | null {
  const profile = profileStorage.getProfile() as {
    duAnId?: number | string
    duAn?: { id?: number | string }
    chiNhanh?: { duAnId?: number | string; duAn?: { id?: number | string } }
  } | null
  const candidates = [
    profile?.duAn?.id,
    profile?.duAnId,
    profile?.chiNhanh?.duAnId,
    profile?.chiNhanh?.duAn?.id,
  ]
  for (const raw of candidates) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

/** Chỉ giữ nhóm chi nhánh thuộc một dự án. Không xác định được dự án → []. */
export function filterChiNhanhMangGroupsByDuAn(
  groups: ChiNhanhMangGroup[],
  duAnId?: number | null,
): ChiNhanhMangGroup[] {
  const id = duAnId == null ? currentDuAnIdFromProfile() : duAnId
  if (id == null || !Number.isFinite(id)) return []
  return groups.filter((g) => g.mangId === id)
}

/**
 * Chi nhánh trong dự án đang đứng (không lẫn chi nhánh dự án khác).
 * Dùng cho form hàng hóa / phạm vi áp dụng.
 */
export async function loadChiNhanhMangGroupsForCurrentDuAn(): Promise<ChiNhanhMangGroup[]> {
  const groups = await loadChiNhanhMangGroups()
  return filterChiNhanhMangGroupsByDuAn(groups)
}

/**
 * Chi nhánh phẳng trong mảng đang đứng — chỉ tên/mã CN (không ghép tên mảng).
 * Dùng dropdown chọn chi nhánh nhận / phạm vi trong cùng dự án.
 */
export async function loadChiNhanhOptionsForCurrentDuAn(): Promise<ChiNhanhOption[]> {
  const groups = await loadChiNhanhMangGroupsForCurrentDuAn()
  return groups.flatMap((g) => g.chiNhanhs).map((c) => ({
    id: c.id,
    label: c.label,
  }))
}
